/**
 * FishingEventBanner
 * Slides in from top when a fishing event triggers.
 * Color-coded: green for positive, red for negative, gold for lucky.
 * Auto-dismisses after 3 seconds.
 */

import { useEffect, useState } from 'react';
import { FishingEvent, FishingEventType, GameEvent } from '@/types';
import { eventBus } from '@/services/EventBus';

function getBannerColors(event: FishingEvent): string {
  if (event.isNegative) {
    return 'bg-red-900/90 border-red-500 text-red-100';
  }
  if (event.type === FishingEventType.LUCKY_CATCH) {
    return 'bg-yellow-900/90 border-yellow-500 text-yellow-100';
  }
  return 'bg-emerald-900/90 border-emerald-500 text-emerald-100';
}

export function FishingEventBanner() {
  const [currentEvent, setCurrentEvent] = useState<FishingEvent | null>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const unsub = eventBus.on(GameEvent.FISHING_EVENT_TRIGGERED, ({ event }) => {
      setCurrentEvent(event);
      setVisible(true);
    });

    return unsub;
  }, []);

  // Auto-dismiss after 3 seconds
  useEffect(() => {
    if (!visible) return;

    const timer = setTimeout(() => {
      setVisible(false);
    }, 3000);

    return () => clearTimeout(timer);
  }, [visible, currentEvent]);

  if (!currentEvent || !visible) {
    return null;
  }

  const colors = getBannerColors(currentEvent);

  return (
    <div
      className={`w-full max-w-md mx-auto rounded-lg border-2 px-4 py-3 shadow-lg animate-in slide-in-from-top duration-300 ${colors}`}
    >
      <div className="flex items-center gap-3">
        <span className="text-2xl">{currentEvent.emoji}</span>
        <div className="flex-1 min-w-0">
          <div className="font-bold text-sm">{currentEvent.name}</div>
          <div className="text-xs opacity-90">{currentEvent.description}</div>
        </div>
      </div>
    </div>
  );
}
